import { useEffect } from "react";

const ChatbotWidget = () => {
  useEffect(() => {
    // SSR guard - prevent execution on server
    if (typeof window === "undefined") return;
    if (window.chatbotInitialized) return;

    // Safe localStorage clear with error handling
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.clear();
      }
    } catch (error) {
      console.warn("localStorage not accessible:", error);
    }

    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/rasa-webchat@1.x.x/lib/index.js";
    script.async = true;

    script.onload = () => {
      try {
        window.WebChat.default(
          {
            title: "Unified Bot 🤖",
            subtitle: "Your AI Assistant from UFS",
            initPayload: "/greet",
            customData: { language: "en" },
            socketUrl: "https://rasa-ufs.duckdns.org",
            socketPath: "/socket.io/",
            profileAvatar:
              "https://cdn-icons-png.flaticon.com/512/4712/4712035.png",
            showMessageDate: true,
            showFullScreenButton: true,
            inputTextFieldHint: "Type your message...",
            params: { storage: "local" },
          },
          null
        );

        // Keep checking for header until found (with timeout)
        let attempts = 0;
        const maxAttempts = 40; // 20 seconds max
        const checkHeaderInterval = setInterval(() => {
          attempts++;
          const header = document.querySelector(".rw-header");

          if (header) {
            addMenuToHeader(header);
            clearInterval(checkHeaderInterval);
          } else if (attempts >= maxAttempts) {
            console.warn("Chatbot header not found after timeout");
            clearInterval(checkHeaderInterval);
          }
        }, 500);

        window.chatbotInitialized = true;
      } catch (error) {
        console.error("Error initializing chatbot:", error);
      }
    };

    script.onerror = () => {
      console.error("Failed to load rasa-webchat script");
    };

    document.head.appendChild(script);

    // Cleanup function
    return () => {
      // Note: Webchat doesn't provide a destroy method, but we can prevent re-initialization
      // The script remains in DOM but won't re-initialize due to the flag
    };
  }, []);

  const addMenuToHeader = (header) => {
    if (!header || header.querySelector(".custom-menu")) return;

    // Menu button
    const menu = document.createElement("div");
    menu.className = "custom-menu";
    menu.innerHTML = "⋮";
    menu.style.cssText = `
      position:absolute;
      top:8px;
      right:12px;
      cursor:pointer;
      font-size:22px;
      font-weight:bold;
      color:white;
      user-select:none;
      z-index:1000;
    `;

    // Menu container
    const menuOptions = document.createElement("div");
    menuOptions.className = "menu-options";
    menuOptions.style.cssText = `
      display:none;
      position:absolute;
      top:35px;
      right:5px;
      background:white;
      border:1px solid #ddd;
      border-radius:5px;
      box-shadow:0 2px 8px rgba(0,0,0,0.2);
      z-index:9999;
      width:180px;
    `;

    // Buttons
    menuOptions.innerHTML = `
      <button style="width:100%;padding:10px;border:none;background:white;text-align:left;cursor:pointer;border-bottom:1px solid #eee;">📥 Download Transcript</button>
      <button style="width:100%;padding:10px;border:none;background:white;text-align:left;cursor:pointer;">🗑 Clear Conversation</button>
    `;

    // Add hover effects
    Array.from(menuOptions.children).forEach((btn) => {
      btn.addEventListener("mouseenter", () => {
        btn.style.background = "#f5f5f5";
      });
      btn.addEventListener("mouseleave", () => {
        btn.style.background = "white";
      });
    });

    // Toggle menu
    const toggleMenu = (e) => {
      e.stopPropagation();
      menuOptions.style.display =
        menuOptions.style.display === "block" ? "none" : "block";
    };
    menu.addEventListener("click", toggleMenu);

    // Close on outside click
    const closeMenu = () => {
      menuOptions.style.display = "none";
    };
    document.addEventListener("click", closeMenu);

    // Download transcript
    menuOptions.children[0].addEventListener("click", () => {
      try {
        const messages = document.querySelectorAll(".rw-message-text");
        let transcript = "Chat Transcript\n";
        transcript += "Generated: " + new Date().toLocaleString() + "\n";
        transcript += "=".repeat(50) + "\n\n";

        messages.forEach((msg, index) => {
          const isBot = msg.closest(".rw-from-response");
          const sender = isBot ? "Bot" : "You";
          transcript += `[${sender}]: ${msg.innerText}\n\n`;
        });

        const blob = new Blob([transcript], { type: "text/plain" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `chat_transcript_${Date.now()}.txt`;
        link.click();

        // Cleanup
        URL.revokeObjectURL(link.href);
      } catch (error) {
        console.error("Error downloading transcript:", error);
        alert("Failed to download transcript. Please try again.");
      }

      menuOptions.style.display = "none";
    });

    // Clear conversation
    menuOptions.children[1].addEventListener("click", () => {
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.clear();
        }

        const chatWindow = document.querySelector(".rw-conversation-container");
        if (chatWindow) {
          const messagesContainer = chatWindow.querySelector(
            ".rw-messages-container"
          );
          if (messagesContainer) {
            messagesContainer.innerHTML =
              "<div style='padding:20px;text-align:center;color:#666;'>Conversation cleared. Refresh to start a new chat.</div>";
          }
        }
      } catch (error) {
        console.error("Error clearing conversation:", error);
        alert("Failed to clear conversation. Please try again.");
      }

      menuOptions.style.display = "none";
    });

    header.appendChild(menu);
    header.appendChild(menuOptions);

    // Store cleanup function
    header._menuCleanup = () => {
      menu.removeEventListener("click", toggleMenu);
      document.removeEventListener("click", closeMenu);
    };
  };

  return null;
};

export default ChatbotWidget;
